import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import PermissionRequiredBanner from '@/components/PermissionRequiredBanner';
import Icon from '@/components/Icon';
import {
  cancelCycle,
  deriveProgress,
  getCycle,
  listCycleItems,
  previewDelta,
  setItemCount,
  setItemSkipped,
  submitCycle,
  itemState,
  type ReconciliationCycleRow,
  type ReconciliationItemWithProduct,
  type SubmitResult,
} from '@/lib/reconciliation';

// ============================================================
// ReconciliationCyclePage — Phase 3, Epic 3B (walk-through)
// ============================================================
// Two modes derived from the cycle's status:
//
//   - in_progress: editable walk-through. Per-product count
//     stepper / freeform input, skip toggle, prev/next nav,
//     progress header, submit + cancel actions at the bottom.
//
//   - submitted / cancelled: read-only summary. Per-item rows
//     show baseline → submitted with delta annotation. No
//     edits possible (RLS would refuse them anyway).
//
// One-product-at-a-time walk-through over a paginated list
// surface. The first uncounted/unskipped item is auto-focused
// on load; "Next" advances to the next pending; "Prev"
// rewinds. A "Jump to" list at the bottom (collapsed by
// default) shows all items with their state — handy for
// returning to a specific item after a phone-interruption.
//
// All item edits are optimistic-then-confirmed: local state
// moves on tap, server write is awaited, error reverts the
// local change.
// ============================================================

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'not_found' }
  | {
      kind: 'loaded';
      cycle: ReconciliationCycleRow;
      items: ReconciliationItemWithProduct[];
    };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; result: SubmitResult }
  | { kind: 'error'; message: string };

export default function ReconciliationCyclePage() {
  const session = useSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const clinicId = session.activeMembership.clinic_id;

  const canView = hasPermission(session, 'reconciliation.view_history');
  const canEdit = hasPermission(session, 'reconciliation.submit_count');

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [cursor, setCursor] = useState(0);
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setState({ kind: 'loading' });
    try {
      const [cycle, items] = await Promise.all([
        getCycle(clinicId, id),
        listCycleItems(clinicId, id),
      ]);
      if (!cycle) {
        setState({ kind: 'not_found' });
        return;
      }
      setState({ kind: 'loaded', cycle, items });

      // On first load of an in-progress cycle, jump cursor to
      // the first item that hasn't been touched yet. Resume UX.
      if (cycle.status === 'in_progress') {
        const firstPending = items.findIndex(
          (it) => !it.skipped && it.submitted_count === null,
        );
        setCursor(firstPending >= 0 ? firstPending : 0);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load cycle.';
      setState({ kind: 'error', message });
    }
  }, [clinicId, id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ---- Item mutations (optimistic) ----
  const patchLocal = useCallback(
    (itemId: string, patch: Partial<ReconciliationItemWithProduct>) => {
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        const next = prev.items.map((it) =>
          it.id === itemId ? { ...it, ...patch } : it,
        );
        return { ...prev, items: next };
      });
    },
    [],
  );

  const onSetCount = useCallback(
    async (itemId: string, count: number | null) => {
      if (!canEdit) return;
      const prevItem =
        state.kind === 'loaded'
          ? state.items.find((it) => it.id === itemId)
          : undefined;
      patchLocal(itemId, { submitted_count: count, skipped: false });
      try {
        await setItemCount(clinicId, itemId, count);
      } catch (err) {
        // Revert
        if (prevItem) {
          patchLocal(itemId, {
            submitted_count: prevItem.submitted_count,
            skipped: prevItem.skipped,
          });
        }
        const message =
          err instanceof Error ? err.message : 'Failed to save count.';
        setSubmit({ kind: 'error', message });
      }
    },
    [canEdit, clinicId, state, patchLocal],
  );

  const onSetSkipped = useCallback(
    async (itemId: string, skipped: boolean) => {
      if (!canEdit) return;
      const prevItem =
        state.kind === 'loaded'
          ? state.items.find((it) => it.id === itemId)
          : undefined;
      patchLocal(itemId, {
        skipped,
        submitted_count: skipped ? null : prevItem?.submitted_count ?? null,
      });
      try {
        await setItemSkipped(clinicId, itemId, skipped);
      } catch (err) {
        if (prevItem) {
          patchLocal(itemId, {
            submitted_count: prevItem.submitted_count,
            skipped: prevItem.skipped,
          });
        }
        const message =
          err instanceof Error ? err.message : 'Failed to update skip.';
        setSubmit({ kind: 'error', message });
      }
    },
    [canEdit, clinicId, state, patchLocal],
  );

  const onCancel = useCallback(async () => {
    if (!id) return;
    try {
      await cancelCycle(clinicId, id);
      navigate('/reconciliation');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to cancel cycle.';
      setSubmit({ kind: 'error', message });
      setConfirmingCancel(false);
    }
  }, [clinicId, id, navigate]);

  const onSubmit = useCallback(
    async (notes: string | null) => {
      if (!id) return;
      setSubmit({ kind: 'submitting' });
      try {
        const result = await submitCycle(clinicId, id, notes);
        setSubmit({ kind: 'success', result });
        setConfirmingSubmit(false);
        // Refresh to pick up the new status + totals.
        await refresh();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit cycle.';
        setSubmit({ kind: 'error', message });
      }
    },
    [clinicId, id, refresh],
  );

  // ---- Render ----
  if (!canView) {
    return (
      <div className="p-4 pb-6">
        <PermissionRequiredBanner
          permission="reconciliation.view_history"
          message="Reconciliation is restricted at this clinic. Ask your clinic admin if you need access."
        />
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div className="p-4 pb-6">
        <BackLink />
        <div className="text-sm text-slate-500 px-1 mt-3">Loading…</div>
      </div>
    );
  }

  if (state.kind === 'not_found') {
    return (
      <div className="p-4 pb-6">
        <BackLink />
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
          Cycle not found. It may have been cancelled or you may not have
          access.
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="p-4 pb-6">
        <BackLink />
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {state.message}
        </div>
      </div>
    );
  }

  const { cycle, items } = state;
  const isActive = cycle.status === 'in_progress';

  if (!isActive) {
    return (
      <CompletedView
        cycle={cycle}
        items={items}
      />
    );
  }

  return (
    <ActiveView
      cycle={cycle}
      items={items}
      cursor={cursor}
      setCursor={setCursor}
      canEdit={canEdit}
      onSetCount={onSetCount}
      onSetSkipped={onSetSkipped}
      onAskCancel={() => setConfirmingCancel(true)}
      onAskSubmit={() => setConfirmingSubmit(true)}
      confirmingCancel={confirmingCancel}
      confirmingSubmit={confirmingSubmit}
      cancelConfirm={onCancel}
      cancelDismiss={() => setConfirmingCancel(false)}
      submitConfirm={onSubmit}
      submitDismiss={() => setConfirmingSubmit(false)}
      submit={submit}
    />
  );
}

// ---- Active walk-through view -------------------------------------

interface ActiveProps {
  cycle: ReconciliationCycleRow;
  items: readonly ReconciliationItemWithProduct[];
  cursor: number;
  setCursor: (n: number) => void;
  canEdit: boolean;
  onSetCount: (itemId: string, count: number | null) => void;
  onSetSkipped: (itemId: string, skipped: boolean) => void;
  onAskCancel: () => void;
  onAskSubmit: () => void;
  confirmingCancel: boolean;
  confirmingSubmit: boolean;
  cancelConfirm: () => void;
  cancelDismiss: () => void;
  submitConfirm: (notes: string | null) => void;
  submitDismiss: () => void;
  submit: SubmitState;
}

function ActiveView(props: ActiveProps) {
  const {
    cycle,
    items,
    cursor,
    setCursor,
    canEdit,
    onSetCount,
    onSetSkipped,
    onAskCancel,
    onAskSubmit,
    confirmingCancel,
    confirmingSubmit,
    cancelConfirm,
    cancelDismiss,
    submitConfirm,
    submitDismiss,
    submit,
  } = props;

  const progress = useMemo(() => deriveProgress(items), [items]);
  const current = items[cursor];

  if (items.length === 0) {
    return (
      <div className="p-4 pb-6">
        <BackLink />
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">
            No items in scope
          </div>
          <div className="text-xs text-slate-600 mt-1 leading-snug">
            This cycle has no essential, non-dead-stock products to count.
            Cancel this cycle and check the Inventory page — items need to be
            marked essential to appear in a reconciliation.
          </div>
          <button
            type="button"
            onClick={onAskCancel}
            className="mt-3 w-full rounded-xl bg-slate-100 text-slate-700 font-semibold py-2 hover:bg-slate-200 transition"
          >
            Cancel cycle
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-6 space-y-3">
      <BackLink />

      <ProgressHeader progress={progress} cycle={cycle} />

      {submit.kind === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 flex items-start justify-between gap-2">
          <span>{submit.message}</span>
        </div>
      )}

      {current && (
        <ItemEditor
          item={current}
          index={cursor}
          total={items.length}
          canEdit={canEdit}
          onSetCount={(c) => onSetCount(current.id, c)}
          onSetSkipped={(s) => onSetSkipped(current.id, s)}
          onPrev={cursor > 0 ? () => setCursor(cursor - 1) : null}
          onNext={
            cursor < items.length - 1 ? () => setCursor(cursor + 1) : null
          }
          onAdvance={() => {
            // After tapping save/skip, advance to the next
            // pending item if one exists; else just step +1.
            const nextPending = items.findIndex(
              (it, i) =>
                i > cursor && !it.skipped && it.submitted_count === null,
            );
            if (nextPending >= 0) setCursor(nextPending);
            else if (cursor < items.length - 1) setCursor(cursor + 1);
          }}
        />
      )}

      <JumpList items={items} cursor={cursor} setCursor={setCursor} />

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button
          type="button"
          onClick={onAskCancel}
          className="rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold py-2.5 hover:bg-slate-50 transition"
        >
          Cancel cycle
        </button>
        <button
          type="button"
          onClick={onAskSubmit}
          disabled={!canEdit || submit.kind === 'submitting'}
          className="rounded-xl bg-emerald-600 text-white font-semibold py-2.5 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition"
        >
          {submit.kind === 'submitting' ? 'Submitting…' : 'Submit cycle'}
        </button>
      </div>

      {confirmingCancel && (
        <ConfirmDialog
          title="Cancel this cycle?"
          body="Your in-progress counts will be discarded. No inventory adjustments are written. This can't be undone."
          confirmLabel="Cancel cycle"
          confirmTone="danger"
          onConfirm={cancelConfirm}
          onDismiss={cancelDismiss}
        />
      )}

      {confirmingSubmit && (
        <SubmitConfirm
          progress={progress}
          onConfirm={submitConfirm}
          onDismiss={submitDismiss}
          submitting={submit.kind === 'submitting'}
        />
      )}
    </div>
  );
}

// ---- Progress header ----------------------------------------------

function ProgressHeader({
  progress,
  cycle,
}: {
  progress: ReturnType<typeof deriveProgress>;
  cycle: ReconciliationCycleRow;
}) {
  const pct =
    progress.inScope === 0
      ? 0
      : Math.round(
          ((progress.counted + progress.skipped) / progress.inScope) * 100,
        );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">
            Count in progress
          </div>
          <div className="text-base font-semibold text-slate-900 mt-0.5">
            {progress.counted} of {progress.inScope} counted
            {progress.skipped > 0 && (
              <span className="font-normal text-slate-500">
                {' '}
                · {progress.skipped} skipped
              </span>
            )}
          </div>
        </div>
        <div className="text-2xl font-bold text-slate-900">{pct}%</div>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-amber-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[10px] text-slate-500 mt-1.5">
        Started {new Date(cycle.started_at).toLocaleString()}
      </div>
    </div>
  );
}

// ---- Item editor (one at a time) ----------------------------------

interface ItemEditorProps {
  item: ReconciliationItemWithProduct;
  index: number;
  total: number;
  canEdit: boolean;
  onSetCount: (count: number | null) => void;
  onSetSkipped: (skipped: boolean) => void;
  onAdvance: () => void;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
}

function ItemEditor({
  item,
  index,
  total,
  canEdit,
  onSetCount,
  onSetSkipped,
  onAdvance,
  onPrev,
  onNext,
}: ItemEditorProps) {
  const state = itemState(item);
  const delta = previewDelta(item);
  const [draft, setDraft] = useState<string>(
    item.submitted_count === null ? '' : String(item.submitted_count),
  );

  // Resync draft when the item changes (cursor moves).
  useEffect(() => {
    setDraft(item.submitted_count === null ? '' : String(item.submitted_count));
  }, [item.id, item.submitted_count]);

  const draftNum = draft === '' ? null : Number.parseInt(draft, 10);
  const draftValid = draft === '' || (Number.isFinite(draftNum) && draftNum! >= 0);

  const onSave = useCallback(() => {
    if (!draftValid) return;
    onSetCount(draftNum);
    onAdvance();
  }, [draftNum, draftValid, onSetCount, onAdvance]);

  const onSkip = useCallback(() => {
    onSetSkipped(true);
    onAdvance();
  }, [onSetSkipped, onAdvance]);

  const onUnskip = useCallback(() => {
    onSetSkipped(false);
  }, [onSetSkipped]);

  const onStep = useCallback(
    (n: number) => {
      const base = draftNum ?? item.product.count;
      const next = Math.max(0, base + n);
      setDraft(String(next));
    },
    [draftNum, item.product.count],
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Item {index + 1} of {total}
        </div>
        <StateBadge state={state} />
      </div>

      <div className="text-base font-semibold text-slate-900">
        {item.product.name}
      </div>
      <div className="text-xs text-slate-500 mt-0.5">
        {item.product.uom}
        {item.product.par !== null && ` · par ${item.product.par}`}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <ReadOnlyStat label="System count" value={item.product.count} />
        <ReadOnlyStat label="Baseline" value={item.baseline_count} />
      </div>

      {item.skipped ? (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-sm font-semibold text-slate-700">
            Skipped for this cycle
          </div>
          <div className="text-xs text-slate-500 mt-1 leading-snug">
            No count entered; inventory won't be adjusted for this item.
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={onUnskip}
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white text-slate-700 font-semibold py-1.5 text-sm hover:bg-slate-50 transition"
            >
              Unskip and count
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Actual count on shelf
            </span>
            <div className="mt-1 flex items-stretch gap-2">
              <button
                type="button"
                onClick={() => onStep(-1)}
                disabled={!canEdit}
                className="rounded-xl border border-slate-300 bg-white w-11 flex items-center justify-center hover:bg-slate-50 disabled:opacity-50 transition"
                aria-label="Decrement"
              >
                <Icon name="minus" className="w-4 h-4 text-slate-700" />
              </button>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={!canEdit}
                className={`flex-1 rounded-xl border bg-white px-3 py-2 text-center text-lg font-semibold text-slate-900 disabled:opacity-50 ${
                  draftValid ? 'border-slate-300' : 'border-red-400'
                }`}
                placeholder="—"
              />
              <button
                type="button"
                onClick={() => onStep(1)}
                disabled={!canEdit}
                className="rounded-xl border border-slate-300 bg-white w-11 flex items-center justify-center hover:bg-slate-50 disabled:opacity-50 transition"
                aria-label="Increment"
              >
                <Icon name="plus" className="w-4 h-4 text-slate-700" />
              </button>
            </div>
          </label>

          {delta !== null && draft !== '' && draftValid && draftNum !== item.product.count && (
            <DeltaPreview
              expected={item.product.count}
              entered={draftNum!}
            />
          )}

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onSkip}
              disabled={!canEdit}
              className="rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold py-2 hover:bg-slate-50 disabled:opacity-50 transition"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!canEdit || !draftValid || draft === ''}
              className="rounded-xl bg-action text-white font-semibold py-2 hover:bg-action-hover disabled:bg-slate-300 disabled:cursor-not-allowed transition"
            >
              Save &amp; next
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
        <button
          type="button"
          onClick={onPrev ?? undefined}
          disabled={!onPrev}
          className="text-sm font-medium text-slate-600 disabled:text-slate-300 px-1.5 py-1"
        >
          ← Previous
        </button>
        <button
          type="button"
          onClick={onNext ?? undefined}
          disabled={!onNext}
          className="text-sm font-medium text-slate-600 disabled:text-slate-300 px-1.5 py-1"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function ReadOnlyStat({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) {
  return (
    <div className="rounded-xl bg-slate-50 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="text-lg font-bold text-slate-900 leading-tight">
        {value ?? '—'}
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: ReturnType<typeof itemState> }) {
  const cfg =
    state === 'counted'
      ? { label: 'Counted', cls: 'bg-emerald-100 text-emerald-700' }
      : state === 'skipped'
        ? { label: 'Skipped', cls: 'bg-slate-200 text-slate-700' }
        : { label: 'Pending', cls: 'bg-amber-100 text-amber-700' };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function DeltaPreview({
  expected,
  entered,
}: {
  expected: number;
  entered: number;
}) {
  const delta = entered - expected;
  const sign = delta > 0 ? '+' : '';
  const tone =
    delta < 0
      ? 'text-rose-700 bg-rose-50 border-rose-200'
      : 'text-green-700 bg-green-50 border-green-200';
  const semantic =
    delta < 0
      ? `Inferred usage: ${-delta} used`
      : `Found ${delta} extra (will adjust upward)`;
  return (
    <div className={`mt-2 rounded-lg border p-2 text-xs ${tone}`}>
      <span className="font-semibold">
        {sign}
        {delta}
      </span>{' '}
      vs system. {semantic}.
    </div>
  );
}

// ---- Jump-to list (all items, collapsed by default) ---------------

function JumpList({
  items,
  cursor,
  setCursor,
}: {
  items: readonly ReconciliationItemWithProduct[];
  cursor: number;
  setCursor: (n: number) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="rounded-2xl border border-slate-200 bg-white"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition flex items-center justify-between gap-2 list-none [&::-webkit-details-marker]:hidden">
        <span>Jump to item</span>
        <Icon
          name="chevdown"
          className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </summary>
      <div className="border-t border-slate-100 max-h-72 overflow-y-auto">
        {items.map((it, i) => {
          const state = itemState(it);
          const isCursor = i === cursor;
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => setCursor(i)}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 border-b border-slate-100 last:border-b-0 transition ${
                isCursor ? 'bg-green-50' : 'hover:bg-slate-50'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {it.product.name}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  baseline {it.baseline_count}
                  {state === 'counted' &&
                    ` → entered ${it.submitted_count}`}
                </div>
              </div>
              <StateBadge state={state} />
            </button>
          );
        })}
      </div>
    </details>
  );
}

// ---- Submit confirmation dialog -----------------------------------

function SubmitConfirm({
  progress,
  onConfirm,
  onDismiss,
  submitting,
}: {
  progress: ReturnType<typeof deriveProgress>;
  onConfirm: (notes: string | null) => void;
  onDismiss: () => void;
  submitting: boolean;
}) {
  const [notes, setNotes] = useState('');
  const remaining = progress.remaining;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 space-y-3">
        <div>
          <div className="text-base font-semibold text-slate-900">
            Submit this cycle?
          </div>
          <div className="text-xs text-slate-600 mt-1 leading-snug">
            {progress.counted} items counted, {progress.skipped} skipped.
            {remaining > 0 && (
              <span className="text-amber-700 font-medium">
                {' '}
                {remaining} item{remaining === 1 ? '' : 's'} still pending —
                pending items are treated as skipped and won't adjust
                inventory.
              </span>
            )}
          </div>
        </div>

        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 resize-none"
            placeholder="Anything worth recording about this count…"
          />
        </label>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={onDismiss}
            disabled={submitting}
            className="rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold py-2.5 hover:bg-slate-50 disabled:opacity-50 transition"
          >
            Keep counting
          </button>
          <button
            type="button"
            onClick={() => onConfirm(notes.trim() || null)}
            disabled={submitting}
            className="rounded-xl bg-emerald-600 text-white font-semibold py-2.5 hover:bg-emerald-700 disabled:bg-slate-300 transition"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmTone,
  onConfirm,
  onDismiss,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  confirmTone: 'danger' | 'primary';
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const confirmCls =
    confirmTone === 'danger'
      ? 'bg-rose-600 hover:bg-rose-700'
      : 'bg-action hover:bg-action-hover';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 space-y-3">
        <div className="text-base font-semibold text-slate-900">{title}</div>
        <div className="text-sm text-slate-600 leading-snug">{body}</div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold py-2.5 hover:bg-slate-50 transition"
          >
            Never mind
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-xl text-white font-semibold py-2.5 transition ${confirmCls}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Completed / cancelled read-only view -------------------------

function CompletedView({
  cycle,
  items,
}: {
  cycle: ReconciliationCycleRow;
  items: readonly ReconciliationItemWithProduct[];
}) {
  const wasCancelled = cycle.status === 'cancelled';
  const sorted = useMemo(() => {
    // Items with non-zero submitted-vs-baseline delta first.
    const copy = [...items];
    copy.sort((a, b) => {
      const da = a.submitted_count === null
        ? 0
        : Math.abs(a.submitted_count - a.baseline_count);
      const db = b.submitted_count === null
        ? 0
        : Math.abs(b.submitted_count - b.baseline_count);
      if (da !== db) return db - da;
      return a.product.name.localeCompare(b.product.name);
    });
    return copy;
  }, [items]);

  return (
    <div className="p-4 pb-6 space-y-3">
      <BackLink />

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${
              wasCancelled
                ? 'bg-slate-200 text-slate-700'
                : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {wasCancelled ? 'Cancelled' : 'Submitted'}
          </span>
          <span className="text-xs text-slate-500">
            {new Date(
              cycle.submitted_at ?? cycle.cancelled_at ?? cycle.started_at,
            ).toLocaleString()}
          </span>
        </div>
        {!wasCancelled && cycle.totals_in_scope !== null && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            <ReadOnlyStat label="Counted" value={cycle.totals_counted} />
            <ReadOnlyStat label="Skipped" value={cycle.totals_skipped} />
            <ReadOnlyStat
              label="Adjustments"
              value={cycle.totals_with_delta}
            />
          </div>
        )}
        {cycle.notes && (
          <div className="mt-3 rounded-lg bg-slate-50 p-2.5 text-sm text-slate-700">
            {cycle.notes}
          </div>
        )}
      </div>

      <div>
        <div className="px-1 mb-2">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Items
          </h2>
        </div>
        <div className="space-y-2">
          {sorted.map((it) => (
            <CompletedRow key={it.id} item={it} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CompletedRow({ item }: { item: ReconciliationItemWithProduct }) {
  const state = itemState(item);
  const delta =
    state === 'counted'
      ? item.submitted_count! - item.baseline_count
      : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {item.product.name}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            baseline {item.baseline_count}
            {state === 'counted' && ` · entered ${item.submitted_count}`}
            {state === 'skipped' && ' · skipped'}
            {state === 'pending' && ' · not counted'}
          </div>
        </div>
        {delta !== null && delta !== 0 && (
          <div
            className={`text-sm font-bold ${
              delta < 0 ? 'text-rose-700' : 'text-green-700'
            }`}
          >
            {delta > 0 ? '+' : ''}
            {delta}
          </div>
        )}
        <StateBadge state={state} />
      </div>
    </div>
  );
}

// ---- Shared chrome ------------------------------------------------

function BackLink() {
  return (
    <Link
      to="/reconciliation"
      className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
    >
      <Icon name="chevright" className="w-4 h-4 rotate-180" />
      Back
    </Link>
  );
}
